import type { Properties as CSSProperties } from "csstype";
import styled from "styled-components";
import { CloseLinear } from "../Iconsax/CloseLinear.js";
import { Text } from "../Text/Text.js";
import { rem } from "../Theme/Theme.constants.js";
import type { Colors, ITheme } from "../Theme/Theme.types.js";
import { StyleToastProps } from "./Toast.types.js";

function isThemeColor(theme: Pick<ITheme, "colors">, color: string): color is keyof Colors {
    return Object.keys(theme.colors).includes(color);
}

export const CustomIconContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
`;

function getWidth(props: Pick<StyleToastProps, "size">) {
    switch (props.size) {
        case "small":
            return rem(16);
        case "regular":
            return rem(28);
        default:
            return rem(32.5);
    }
}

export const Container = styled.div<StyleToastProps>`
    margin-bottom: ${(props) => `${props.theme.spacing["8"]}`};
    background-color: ${(props) => props.theme.grayscale.white};
    border-radius: ${(props) => props.theme.radius.m};
    padding: ${(props) => props.theme.spacing[props.stretchContent ? "12" : "16"]};
    box-shadow: ${(props) => props.theme.shadows.popover};
    align-items: ${(props) => props.contentVerticalAlignment};
    gap: ${(props) => props.theme.spacing["12"]};
    transition: opacity ${({ transitionDuration }) => `${transitionDuration}ms`} cubic-bezier(0.4, 0, 0.2, 1);
    display: ${(props) => (props.transitionState === "exited" ? "none" : "flex")};
    opacity: ${({ transitionState }) => (transitionState === "entered" ? 1 : 0)};
    width: ${(props) => getWidth(props)};
`;

export const Border = styled.div<Pick<StyleToastProps, "color"> & { height?: CSSProperties["height"] }>`
    height: ${(props) => props.height || props.theme.rem(3)};
    border-radius: ${(props) => props.theme.radius.l};
    background-color: ${(props): string | undefined => {
        const { color, theme } = props;
        if (isThemeColor(theme, color) && "100" in theme.colors[color]) {
            // @ts-expect-error - TODO: fix this
            return theme.colors[color];
        }
        return undefined;
    }};
    flex: 0 0 ${({ theme }) => theme.spacing["4"]};
`;

export const Body = styled.div<{ stretchContent?: boolean }>`
    display: flex;
    flex-direction: column;
    width: 100%;
    max-width: 100%;
    min-width: 0;
    ${({ stretchContent, theme }) => stretchContent && `gap: ${theme.spacing["8"]}; justify-content: center;`};
`;

export const Title = styled(Text)<Pick<StyleToastProps, "color"> & { capitalizeTitle: boolean }>`
    color: ${(props) => {
        const {
            color,
            theme: { colors },
        } = props;

        if (!isThemeColor({ colors }, color)) {
            return color;
        }
        const colorValue = colors[color];

        if (!("100" in colorValue)) {
            return color;
        }

        return colorValue["100"];
    }};
    ${({ capitalizeTitle }) => (capitalizeTitle ? "text-transform: capitalize;" : "")};
`;

export const CloseIcon = styled(CloseLinear)`
    cursor: pointer;
    height: max-content;
`;
